// @givmo/board — entering one historical record into a backfill session.
//
// ══ WHAT THIS FORM IS AND IS NOT ═══════════════════════════════════════════════════════════════
//
// It posts to `POST …/imported-records` — the record core's ONE import door — with the session's id
// attached. There is no second write path and no session-scoped writer: entering a record under a
// session IS the ordinary import with an `import_batch_id`, which is why an organization bringing in one
// stray document and an organization bringing in thirty years of minutes take the same route.
//
// An imported record lands IMMUTABLE on entry and is never routed through submit/approve — nobody
// here approved a resolution passed in 2015. So this form is the whole act: what it sends is what the
// organization's minute book will say, and the only correction afterwards is a correction record.
//
// THE FILE GOES IN WITH THE RECORD (CBG-AI5-PROPOSE, frame h1; Jonathan, 2026-10-18 17:30 PDT, "2)
// yes"; backend `board-ai-proposals.md` §6.4). RC8 deferred the upload and took a reference string in
// its place; this form now takes the scan or original itself, through the same kind of signed slot and
// the same 50 MB as the proposed batch's drop-in. The file field takes the reference field's place:
// "Where this came from" already holds the organization's own account of where the original is kept.
// The file is chosen here, refused at once over 50 MB or of a kind Board doesn't take, and goes up only
// on "Record it": the page asks for a slot in this batch, PUTs the file, and sends its upload id with
// the record; the import door checks the file and copies it to the record's own key before the record
// is entered, so a record and its file are one act. A record can't take a file afterwards, and the
// field's note says so before the act. After one of the record's own refusals a retry sends the same
// upload (the copy is reused); after the file's own refusal, or an upload already used, the next
// attempt uploads afresh. Cancelling after an upload asks Board to delete it (best-effort; Board's
// age sweep catches what this misses).
//
// ⚠ A CLOSED SESSION IS OFFERED WITH NO PARTICIPANTS, DELIBERATELY. The backend accepts
// `executive_session` on an imported record but the module offers NO reconstruction of who was in a
// room it never observed — a participant roster typed in years later is a false record of presence.
// Whoever entered it is the one it is sent to (the record core's authorship arm), and every current
// director may open it, each opening recorded in its history (D-CBG-25 as amended). The form says
// exactly that before the officer chooses (CBG-UX-COPY5's words, D-CBG-52).
//
// ⚠ A REPLAY IS NOT AN ERROR. Inside a session the import de-duplicates by content address and
// answers 200 with the record already entered. The caller renders {@link importOutcomeLine}, which
// says "already in the book" and names the one way to record two genuinely different documents that
// happen to match in every hashed field.

import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  firstErrorCode,
  isGivmoError,
  type BoardProposalFileContentType,
  type OrgBoardGoverningDocumentOut,
  type OrgBoardRecordOut,
} from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  Icon,
  Select,
  Switch,
  TextField,
  Textarea,
} from '@givmo/design-system';
import { FileText } from 'lucide-react';
import { FieldNote } from '../../components/field-note';
import {
  DOCUMENT_KINDS,
  chainVersions,
  extractGoverningDocuments,
  requiresLabel,
} from '../govdocs/govdocs-decode';
import { isTheRecord, recordDateWording, recordKindLabel } from '../records/records-decode';
import { displayDate } from '../../lib/display';
import { IMPORTABLE_RECORD_KINDS, todayAsDateInput } from './import-decode';
import { useBoardOperations } from '../../board-runtime';
// CBG-AI5-PROPOSE (frame h1): the file's check, its upload and its words.
import {
  FILE_ACCEPT,
  MAX_FILE_BYTES,
  chosenFileType,
  fileCheckRefusal,
  putToSlot,
} from '../proposals/proposal-decode';
import { fileKindWord, proposalWords as pw } from '../proposals/proposal-words';
import { fileSize } from '../returns/return-decode';

/** The record kinds the backend accepts as the act that adopted another record. */
const ADOPTING_RECORD_KINDS: readonly string[] = ['minutes', 'resolution', 'written_consent'];

/** The import door's refusals that leave no upload to reuse: Board's check of the file deleted it,
 *  or a committed record already names it (design §6.4). */
const UPLOAD_SPENT = new Set(['wrong_type', 'too_large', 'unreadable', 'upload_already_used']);

/** A refusal this form words itself (the file's kind or size, a PUT storage refused). */
class SaidError extends Error {}

/** An upload in this batch, kept for a retry of the same file. */
interface Uploaded {
  file: File;
  uploadId: string;
  contentType: BoardProposalFileContentType;
}

export interface ImportedRecordDialogProps {
  orgRef: string;
  batchId: string;
  onClose: () => void;
  onEntered: (record: OrgBoardRecordOut) => void;
}

export function ImportedRecordDialog({
  orgRef,
  batchId,
  onClose,
  onEntered,
}: ImportedRecordDialogProps) {
  const operations = useBoardOperations();
  const [recordKind, setRecordKind] = useState('minutes');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [occurredOn, setOccurredOn] = useState(() => todayAsDateInput());
  const [sourceNote, setSourceNote] = useState('');
  // CBG-AI5-PROPOSE (frame h1): the chosen file, and its upload once it went up.
  const [file, setFile] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState<Uploaded | null>(null);
  const [sendingFile, setSendingFile] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [closedSession, setClosedSession] = useState(false);
  const [meetingRecordId, setMeetingRecordId] = useState('');
  const [adoptedByRecordId, setAdoptedByRecordId] = useState('');
  const [documentKind, setDocumentKind] = useState('bylaws');
  const [documentLabel, setDocumentLabel] = useState('');
  const [supersedesRecordId, setSupersedesRecordId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isGoverningDocument = recordKind === 'governing_document';
  const needsLabel = requiresLabel(documentKind);

  // The 100 most recent records this reader may see, newest first. A backfill names a meeting or an
  // adopting act it entered MOMENTS ago, so the recent window is the picker's real population.
  const recordsQuery = useQuery({
    queryKey: ['board-records', orgRef, 'linkable'],
    queryFn: async (): Promise<OrgBoardRecordOut[]> => {
      const { data } = await operations.officerListBoardRecords({
        path: { org_ref: orgRef },
        query: { 'page[limit]': 100 },
        throwOnError: true,
      });
      const rows = Array.isArray(data)
        ? data
        : ((data as { data?: OrgBoardRecordOut[] })?.data ?? []);
      return rows.filter(isTheRecord);
    },
  });

  const registryQuery = useQuery({
    queryKey: ['board-governing-documents', orgRef],
    enabled: isGoverningDocument,
    queryFn: async (): Promise<OrgBoardGoverningDocumentOut[]> => {
      const { data } = await operations.officerListBoardGoverningDocuments({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return extractGoverningDocuments(data);
    },
  });

  const meetings = (recordsQuery.data ?? []).filter((record) => record.record_kind === 'meeting');
  const adoptingRecords = (recordsQuery.data ?? []).filter((record) =>
    ADOPTING_RECORD_KINDS.includes(record.record_kind),
  );

  // Every version already recorded for the instrument being entered, in the order the backend walked
  // them — so an officer working oldest-first can name the one this replaces.
  const predecessors = useMemo(() => {
    const document = (registryQuery.data ?? []).find(
      (entry) =>
        entry.document_kind === documentKind &&
        (needsLabel ? (entry.label ?? '').trim() === documentLabel.trim() : true),
    );
    return document === undefined ? [] : chainVersions(document);
  }, [registryQuery.data, documentKind, documentLabel, needsLabel]);

  /** An upload no attempt will name again: Board is asked to delete it, best-effort. */
  const dropUpload = () => {
    if (uploaded === null) return;
    void operations
      .officerDeleteBoardImportFileUpload({
        path: { org_ref: orgRef, batch_id: batchId, upload_id: uploaded.uploadId },
      })
      .catch(() => undefined);
    setUploaded(null);
  };

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null;
    // Cleared, so choosing the SAME file again still fires a change event.
    event.target.value = '';
    if (chosen === null) return;
    // Before anything uploads (design §3.3): a person's slot carries no size range.
    if (chosenFileType(chosen) === null) {
      setError(pw.addFileKinds);
      return;
    }
    if (chosen.size > MAX_FILE_BYTES) {
      setError(pw.chosenTooLarge);
      return;
    }
    setError(null);
    dropUpload();
    setFile(chosen);
  };

  const cancel = () => {
    dropUpload();
    onClose();
  };

  /** The file's part of the body: uploaded first (slot, then PUT), or the kept upload reused. */
  const fileForTheRecord = async () => {
    if (file === null) return null;
    let upload = uploaded !== null && uploaded.file === file ? uploaded : null;
    if (upload === null) {
      const contentType = chosenFileType(file);
      if (contentType === null) throw new SaidError(pw.addFileKinds);
      setSendingFile(true);
      try {
        const issued = await operations.officerIssueBoardImportFileUploadUrl({
          path: { org_ref: orgRef, batch_id: batchId },
          body: { file_name: file.name, content_type: contentType },
          throwOnError: true,
        });
        if (file.size > issued.data.max_byte_size) throw new SaidError(pw.chosenTooLarge);
        const uploadId = issued.data.upload_id ?? null;
        if (uploadId === null || !(await putToSlot(issued.data, file))) {
          throw new SaidError(pw.fileDidNotArrive);
        }
        upload = { file, uploadId, contentType };
        setUploaded(upload);
      } finally {
        setSendingFile(false);
      }
    }
    return { upload_id: upload.uploadId, file_name: file.name, content_type: upload.contentType };
  };

  const enter = useMutation({
    mutationFn: async (): Promise<OrgBoardRecordOut> => {
      const fileBody = await fileForTheRecord();
      const { data } = await operations.officerImportBoardRecord({
        path: { org_ref: orgRef },
        body: {
          record_kind: recordKind as 'minutes',
          title: title.trim(),
          body,
          source_note: sourceNote.trim(),
          import_batch_id: batchId,
          confidentiality: closedSession ? 'executive_session' : 'standard',
          ...(occurredOn.length > 0 ? { occurred_on: occurredOn } : {}),
          ...(fileBody !== null ? { file: fileBody } : {}),
          ...(meetingRecordId.length > 0 ? { meeting_record_id: meetingRecordId } : {}),
          // The adopting pointer arrives EITHER at the top level OR inside the descriptor, never
          // both — the backend answers 422 for the pair, and both paths validate the same way.
          ...(adoptedByRecordId.length > 0 && !isGoverningDocument
            ? { adopted_by_record_id: adoptedByRecordId }
            : {}),
          ...(isGoverningDocument
            ? {
                governing_document: {
                  document_kind: documentKind as 'bylaws',
                  ...(needsLabel ? { label: documentLabel.trim() } : {}),
                  ...(supersedesRecordId.length > 0
                    ? { supersedes_record_id: supersedesRecordId }
                    : {}),
                  ...(adoptedByRecordId.length > 0
                    ? { adopted_by_record_id: adoptedByRecordId }
                    : {}),
                },
              }
            : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (record) => onEntered(record),
    // Rendered VERBATIM — the backend's refusals here already say what to do, and the two an officer
    // will actually meet ("this import session has been closed…", "a document's versions are recorded
    // oldest first") are written for exactly this moment.
    onError: (caught) => {
      if (caught instanceof SaidError) {
        setError(caught.message);
        return;
      }
      // The file's own refusal (Board's check deleted the upload) or an upload already used: the next
      // attempt uploads afresh. The record's own refusals keep the upload for a retry.
      if (isGivmoError(caught) && UPLOAD_SPENT.has(firstErrorCode(caught) ?? '')) setUploaded(null);
      setError(
        fileCheckRefusal(caught, file !== null ? chosenFileType(file) : null) ??
          (isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.'),
      );
    },
  });

  const canSave =
    title.trim().length > 0 &&
    sourceNote.trim().length > 0 &&
    (!isGoverningDocument || !needsLabel || documentLabel.trim().length > 0) &&
    !enter.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !enter.isPending && cancel()}>
      <Dialog.Content
        aria-label="Record something from before"
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="import-record-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record something from before
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          This goes straight into your minute book as it stands — it is not a draft and it is not
          approved here, because your board already did that at the time.
        </p>

        <Select
          label="What is it"
          appearance="saas"
          value={recordKind}
          onValueChange={(next) => next !== null && setRecordKind(next)}
          options={IMPORTABLE_RECORD_KINDS.map((entry) => ({
            value: entry.value,
            label: entry.label,
          }))}
          testId="import-record-kind"
        />

        {isGoverningDocument ? (
          <>
            <Select
              label="Which instrument"
              appearance="saas"
              value={documentKind}
              onValueChange={(next) => {
                if (next === null) return;
                setDocumentKind(next);
                setSupersedesRecordId('');
              }}
              options={DOCUMENT_KINDS.map((entry) => ({ value: entry.value, label: entry.label }))}
              testId="import-document-kind"
            />
            {needsLabel ? (
              <TextField
                label="Which one"
                value={documentLabel}
                maxLength={200}
                onChange={(event) => setDocumentLabel(event.target.value)}
                testId="import-document-label"
              />
            ) : null}
            <Select
              label="Does this replace a version you have already recorded?"
              appearance="saas"
              value={supersedesRecordId}
              onValueChange={(next) => setSupersedesRecordId(next ?? '')}
              options={[
                { value: '', label: 'No — this is the earliest one you are recording' },
                ...predecessors.map((version) => ({
                  value: version.record_id,
                  label: `Replaces ${(version.record?.title ?? '').trim() || 'an earlier version'}${
                    version.adopted_on ? ` (${displayDate(version.adopted_on)})` : ''
                  }`,
                })),
              ]}
              testId="import-document-supersedes"
            />
            <FieldNote testId="import-oldest-first">
              Record a document’s versions oldest first — each one names the version it replaces, so
              the earlier one has to be in your minute book already.
            </FieldNote>
          </>
        ) : null}

        <TextField
          label="Title"
          value={title}
          maxLength={300}
          onChange={(event) => setTitle(event.target.value)}
          testId="import-title"
        />

        <TextField
          label={recordDateWording(recordKind).label}
          type="date"
          value={occurredOn}
          onChange={(event) => setOccurredOn(event.target.value)}
          testId="import-occurred-on"
        />
        <FieldNote>
          The meeting date, or the date the document was adopted — not today. It is what your record
          is kept from.
        </FieldNote>

        <Textarea
          label="What it says"
          rows={10}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          testId="import-body"
        />

        <TextField
          label="Where this came from"
          value={sourceNote}
          maxLength={2000}
          onChange={(event) => setSourceNote(event.target.value)}
          testId="import-source-note"
        />
        <FieldNote>
          Your own account of where it came from — “minute book, page 14”. Required, so a reader can
          always tell this apart from something written here. It is also how you tell two otherwise
          identical documents apart.
        </FieldNote>

        {/* CBG-AI5-PROPOSE (frame h1): the file, in the reference field's place. */}
        <div className="flex flex-col" data-testid="import-file">
          <span className="mb-8 font-sans text-s12 font-w600 tracking-givmo text-black">
            {pw.byHandFileLabel}
          </span>
          <input
            ref={fileInput}
            type="file"
            accept={FILE_ACCEPT}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={chooseFile}
            data-testid="import-file-input"
          />
          {file !== null ? (
            <div
              className="flex items-center gap-10 rounded-lg border border-border-subtle px-12 py-10"
              data-testid="import-file-chosen"
            >
              <Icon icon={FileText} size={18} aria-hidden className="shrink-0 text-text-tertiary" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong">
                  {file.name}
                </span>
                <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                  {`${fileKindWord(chosenFileType(file))} · ${fileSize(file.size)}`}
                </span>
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={enter.isPending}
                onClick={() => {
                  dropUpload();
                  setFile(null);
                }}
                testId="import-file-remove"
              >
                {pw.remove}
              </Button>
            </div>
          ) : (
            <div>
              <Button
                appearance="saas"
                tone="secondary"
                disabled={enter.isPending}
                onClick={() => fileInput.current?.click()}
                testId="import-file-add"
              >
                {pw.addTheFile}
              </Button>
            </div>
          )}
        </div>
        <FieldNote testId="import-file-note">{pw.byHandFileNote}</FieldNote>

        {meetings.length > 0 && recordKind !== 'meeting' ? (
          <Select
            label="The meeting this is about (optional)"
            appearance="saas"
            value={meetingRecordId}
            onValueChange={(next) => setMeetingRecordId(next ?? '')}
            options={[
              { value: '', label: 'Not recorded' },
              ...meetings.map((record) => ({ value: record.id, label: record.title })),
            ]}
            testId="import-meeting-link"
          />
        ) : null}

        {adoptingRecords.length > 0 && (isGoverningDocument || recordKind === 'resolution') ? (
          <Select
            label="The record of the act that adopted it (optional)"
            appearance="saas"
            value={adoptedByRecordId}
            onValueChange={(next) => setAdoptedByRecordId(next ?? '')}
            options={[
              { value: '', label: 'Not recorded' },
              ...adoptingRecords.map((record) => ({
                value: record.id,
                label: `${recordKindLabel(record.record_kind)} — ${record.title}`,
              })),
            ]}
            testId="import-adopted-by"
          />
        ) : null}

        <div className="flex flex-col gap-8">
          <label className="flex items-center gap-10">
            <Switch
              appearance="saas"
              checked={closedSession}
              onCheckedChange={setClosedSession}
              testId="import-closed-session"
            />
            <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
              This was a closed session
            </span>
          </label>
          {closedSession ? (
            <FieldNote testId="import-closed-session-note">
              Givmo can’t say who was in a room it wasn’t in, so it won’t record one. Any current
              director can open this record, and its openings are recorded in its history.
            </FieldNote>
          ) : null}
        </div>

        {error !== null ? <ErrorMessage testId="import-record-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={enter.isPending}
            onClick={cancel}
            testId="import-record-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={enter.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              enter.mutate();
            }}
            testId="import-record-save"
          >
            {sendingFile ? pw.addingTheFile : 'Record it'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
